import { useMemo, useRef, useState } from 'react';
import backend from '../services/backend';
import { TableSkeleton } from '../components/Skeleton';
import { ArrowDownTrayIcon, ArrowUpTrayIcon, BeakerIcon, CheckIcon, ChevronDownIcon, PlusIcon } from '@heroicons/react/24/outline';
import { Dropdown, DropdownButton, DropdownDivider, DropdownItem, DropdownLabel, DropdownMenu } from '../catalyst/dropdown';
import SortableTable, { type SortableTableColumn } from '../components/SortableTable';
import type { ApiEndpoints } from '@avisoma/shared';

type CategoryRow = ApiEndpoints<Date>['/api/categories']['GET']['response']['items'][number];
import { PageHeader } from '../components/PageHeader';
import { Link } from '../components/Link';
import CategoryMislabelMap from '../components/categoryMap/CategoryMislabelMap';
import ModelHashBadge from '../components/ModelHashBadge';
import { useNavigate } from 'react-router-dom';
import { Button } from '../catalyst/button';
import pLimit from 'p-limit';
import { useToast } from '../components/Toast';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useKeyPressed } from '../hooks/useKeyPressed';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDateTime } from '../lib/dateFormat';
import { useServerFeatures } from '../hooks/useServerFeatures';
import AuthGuard from '../components/AuthGuard';
import { useAuth } from '../hooks/useAuth';
import ModelRegistryCard from './ModelRegistryCard';
import { useFilterState } from '../components/filters/useFilterState';
import CategoriesFilters from './categories/CategoriesFilters';
import {
  CATEGORIES_FILTER_DEFAULTS,
  hasActiveFilters,
  type CategoriesFilterValues,
} from './categories/categoriesFilters.types';
import { useBatchCategoryAction } from './categories/useBatchCategoryAction';
import { EmptyCategoriesState } from './categories/EmptyCategoriesState';
import { resolveCategoriesViewState } from './categories/categoriesViewState';
import { reclassifyBatchError } from '../lib/reclassifyFailedDocumentsMessage';

const CARD_CLASSES = 'bg-white dark:bg-zinc-800 shadow rounded-lg overflow-hidden';

export default function Categories() {
  useDocumentTitle('Categories');
  const auth = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [showGraph, setShowGraph] = useState(false);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<Set<string>>(new Set());
  const [exportLoading, setExportLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const importInputRef = useRef<HTMLInputElement>(null);

  const altPressed = useKeyPressed('Alt');

  const features = useServerFeatures();
  const trainingEnabled = features?.training === true;
  const canImportModel = auth.hasAccess(['admin']);
  const canExportModel = auth.hasAccess(['admin']);
  const canManageCategories = auth.hasAccess(['admin', 'supervisor']);

  const navigate = useNavigate();

  const { data: response, isLoading: loading, error: fetchError } = useQuery({
    queryKey: ['categories'],
    queryFn: () => backend.listCategories().then((res) => res.data),
  });

  const modelHashQuery = useQuery({
    queryKey: ['system', 'model-hash'],
    queryFn: () => backend.getModelHash().then((res) => res.data),
  });

  const { filters, setFilters, clear: clearFilters } = useFilterState<CategoriesFilterValues>({
    storageKey: 'categories-filters',
    defaults: CATEGORIES_FILTER_DEFAULTS,
    // Filtering is client-side; no server call needed on filter change.
    onFilterChange: () => {},
  });

  const filteredData = useMemo(() => {
    if (!response?.items) return [];

    const anyMatch = filters.name.toLowerCase().split('|').filter(Boolean);
    return response.items.filter((item) => {
      if (filters.name && !anyMatch.some((match) => item.name.toLowerCase().match(new RegExp(match)))) {
        return false;
      }
      if (filters.needsTraining === 'yes' && !item.needsTraining) return false;
      if (filters.needsTraining === 'no' && item.needsTraining) return false;
      if (filters.excluded === 'yes' && !item.excluded) return false;
      if (filters.excluded === 'no' && item.excluded) return false;
      return true;
    });
  }, [response?.items, filters]);

  // Categories matching the name/needsTraining filters — used to dim the map's other dots
  // and narrow its worklist. null = no such filter active (show everything). Excluded
  // categories are dropped from the map server-side, so that dimension isn't applied here.
  const emphasizedCategoryIds = useMemo(() => {
    // Until the categories list has loaded, treat as no filter (null) rather than an empty
    // Set — an empty Set would dim every cluster while the list is still in flight.
    if (!response?.items) return null;
    if (!filters.name && !filters.needsTraining) return null;
    const anyMatch = filters.name.toLowerCase().split('|').filter(Boolean);
    return new Set(
      response.items
        .filter((item) => {
          if (filters.name && !anyMatch.some((match) => item.name.toLowerCase().match(new RegExp(match)))) {
            return false;
          }
          if (filters.needsTraining === 'yes' && !item.needsTraining) return false;
          if (filters.needsTraining === 'no' && item.needsTraining) return false;
          return true;
        })
        .map((item) => item.id),
    );
  }, [response?.items, filters.name, filters.needsTraining]);

  // Checked category ids as a numeric Set for the map (null when none checked). Compare mode
  // is exactly what's checked, independent of the table filters — a name/exclude filter that
  // hides a checked row doesn't change what the map compares. The map header's "Clear" keeps
  // the selection manageable even when those rows are filtered out of view.
  const selectedCategoryIdSet = useMemo(
    () => (selectedCategoryIds.size > 0 ? new Set([...selectedCategoryIds].map(Number)) : null),
    [selectedCategoryIds],
  );

  const columns: SortableTableColumn<CategoryRow>[] = [
    {
      label: 'Name',
      accessor: 'name',
      sortable: true,
      render: (item) => (
        <div className="flex items-center gap-2">
          <Link to={`/categories/${item.id}`} onClick={(e) => e.stopPropagation()}>
            {item.name}
          </Link>
        </div>
      ),
    },
    {
      label: 'Description',
      accessor: 'description',
      sortable: true,
      // Without this a missing description sorts as the literal string "null" and lands
      // in the middle of the alphabet; '' keeps every unset row together at one end.
      sortValue: (item) => item.description ?? '',
      render: (item) => (
        <span
          className="block max-w-xs truncate text-gray-500 dark:text-zinc-400"
          title={item.description ?? ''}
        >
          {item.description ?? '—'}
        </span>
      ),
    },
    {
      label: 'Input Count',
      accessor: 'inputCount',
      sortable: true,
      footer: (rows) => rows.reduce((sum, r) => sum + (r.inputCount ?? 0), 0),
    },
    {
      label: 'Linked Page Count',
      accessor: 'linkedPageCount',
      sortable: true,
      render: (item, index, array) => {
        const value = item.linkedPageCount ?? 0;
        if (array[index].inputCount !== value) {
          return <span className="text-red-500">{value}</span>;
        }
        return value;
      },
      footer: (rows) => {
        const linkedTotal = rows.reduce((sum, r) => sum + (r.linkedPageCount ?? 0), 0);
        const inputTotal = rows.reduce((sum, r) => sum + (r.inputCount ?? 0), 0);
        return linkedTotal !== inputTotal ? <span className="text-red-500">{linkedTotal}</span> : linkedTotal;
      },
    },
    {
      label: 'Embedding Clusters Threshold',
      accessor: 'embeddingClustersThreshold',
      sortable: true,
      render: (row) => row.embeddingClustersThreshold?.toFixed(2) ?? 'N/A',
    },
    {
      label: 'Embedding Clusters Count',
      accessor: 'embeddingClustersCount',
      sortable: true,
      footer: (rows) => rows.reduce((sum, r) => sum + (r.embeddingClustersCount ?? 0), 0),
    },
    {
      label: 'Needs Training',
      accessor: 'needsTraining',
      sortable: true,
      render: (item) => (item.needsTraining ? <span className="text-red-500">Yes</span> : 'No'),
    },
    {
      label: 'Excluded',
      accessor: 'excluded',
      sortable: true,
      render: (item) => (item.excluded ? 'Yes' : 'No'),
    },
    {
      label: 'Classifications',
      accessor: 'classificationsCount',
      sortable: true,
      footer: (rows) => rows.reduce((sum, r) => sum + (r.classificationsCount ?? 0), 0),
    },
    {
      label: 'Recall',
      accessor: 'classificationRatio',
      sortable: true,
      render: (item) => `${(item.classificationRatio * 100).toFixed(2)}%`,
      footer: (rows) => {
        const inputTotal = rows.reduce((sum, r) => sum + (r.inputCount ?? 0), 0);
        if (inputTotal === 0) return '—';
        const weighted = rows.reduce((sum, r) => sum + (r.classificationRatio ?? 0) * (r.inputCount ?? 0), 0);
        return `${((weighted / inputTotal) * 100).toFixed(2)}%`;
      },
    },
    {
      label: 'Created',
      accessor: 'createdAt',
      sortable: true,
      render: (item) => formatDateTime(item.createdAt),
    },
    {
      label: 'Updated At',
      accessor: 'updatedAt',
      sortable: true,
      render: (item) => formatDateTime(item.updatedAt),
    },
  ];

  const refetchCategories = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: ['categories'] }),
      queryClient.invalidateQueries({ queryKey: ['system', 'model-hash'] }),
    ]);

  const rebuildAllCategoriesMutation = useMutation({
    mutationFn: async () => {
      const limit = pLimit(10);
      return Promise.all(
        filteredData.map(async (item) => {
          const category = await limit(async () => backend.getCategory(item.id.toString()));
          await Promise.all(
            category.data.pages.map((page) => limit(async () => backend.pageRetry(page.id, 'pdf-parser'))),
          );
        }),
      );
    },
    onSuccess: () => {
      toast.success('All categories rebuilt successfully');
    },
    onError: (error) => {
      toast.error(error instanceof Error ? error.message : 'Failed to rebuild all categories');
    },
  });

  const { loading: trainLoading, run: handleTrainAllCategories } = useBatchCategoryAction({
    items: filteredData,
    verb: 'Training',
    action: (id) => backend.trainCategory(id),
    errorMessage: 'Failed to train category',
    onSuccess: refetchCategories,
    onAllError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to train all categories'),
  });

  const { loading: reclassifyLoading, run: handleReclassify } = useBatchCategoryAction({
    items: filteredData,
    verb: 'Reclassifying',
    // A 200 response can still be a skip or a partial recompute failure. Raise it so the batch shows the error toast.
    action: (id) =>
      backend.reclassifyCategory(id).then((res) => {
        const message = reclassifyBatchError({
          success: res.data.success,
          failedDocumentIds: res.data.failedDocumentIds,
        });
        if (message) throw new Error(message);
      }),
    errorMessage: 'Failed to reclassify category',
    onSuccess: refetchCategories,
    onAllError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to reclassify all categories'),
  });

  const handleExportModel = async () => {
    setExportLoading(true);
    try {
      const r = await backend.exportModel();
      const disposition = r.headers['content-disposition'] as string | undefined;
      const matched = disposition?.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i)?.[1];
      const filename = matched ?? `model-${new Date().toISOString().slice(0, 10)}.zip`;
      const url = URL.createObjectURL(r.data as Blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to export model');
    } finally {
      setExportLoading(false);
    }
  };

  const handleImportModelFile = async (file: File) => {
    setImportLoading(true);
    try {
      await backend.importModel(file);
      toast.success('Model imported');
      await refetchCategories();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to import model');
    } finally {
      setImportLoading(false);
    }
  };

  const isBusy =
    trainLoading !== false ||
    reclassifyLoading !== false ||
    exportLoading ||
    importLoading ||
    rebuildAllCategoriesMutation.isPending;

  const hasFilters = hasActiveFilters(filters);
  const categoryCount = response?.items.length ?? 0;
  const viewState = resolveCategoriesViewState({
    categoriesStatus: loading ? 'loading' : fetchError ? 'error' : 'success',
    categoryCount,
    filteredCount: filteredData.length,
    hasFilters,
    modelHashStatus: modelHashQuery.isLoading ? 'loading' : modelHashQuery.isError ? 'error' : 'success',
    modelHash: modelHashQuery.data ?? null,
  });
  const showFilters = viewState.kind !== 'empty';
  const showContentActions = viewState.kind === 'ready';
  const showResetAllPages = altPressed && showContentActions;
  const showModelActions = canExportModel || canImportModel;
  const showActionsMenu = showContentActions || showModelActions;

  return (
    <div className="">
      {/* Header */}
      <PageHeader
        title="Categories"
        description="Manage your document classification categories"
        icon={<BeakerIcon className="size-6" />}
      >
        <div className="flex md:items-center gap-2 flex-col md:flex-row w-full md:justify-end">
          <ModelHashBadge
            data={modelHashQuery.data}
            isLoading={modelHashQuery.isLoading}
            isError={modelHashQuery.isError}
          />
          <input
            ref={importInputRef}
            type="file"
            accept=".zip,application/zip"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) handleImportModelFile(file);
              e.target.value = '';
            }}
          />
          <AuthGuard roles={['admin', 'supervisor']}>
            {showActionsMenu && (
              <Dropdown>
                <DropdownButton as={Button} outline disabled={isBusy}>
                  {trainLoading ||
                    reclassifyLoading ||
                    (exportLoading && 'Exporting...') ||
                    (importLoading && 'Importing...') ||
                    (rebuildAllCategoriesMutation.isPending && 'Resetting...') ||
                    'Actions'}
                  <ChevronDownIcon className="size-4" />
                </DropdownButton>
                <DropdownMenu anchor="bottom end">
                  {showContentActions && (
                    <>
                      <DropdownItem onClick={() => setShowGraph(!showGraph)}>
                        <CheckIcon className={showGraph ? undefined : 'invisible'} />
                        <DropdownLabel>Show category map</DropdownLabel>
                      </DropdownItem>
                      <DropdownDivider />
                      <DropdownItem onClick={handleTrainAllCategories} disabled={!trainingEnabled}>
                        <BeakerIcon />
                        <DropdownLabel>Train All Categories</DropdownLabel>
                      </DropdownItem>
                      <DropdownItem onClick={handleReclassify} disabled={!trainingEnabled}>
                        <BeakerIcon />
                        <DropdownLabel>Reclassify All Categories</DropdownLabel>
                      </DropdownItem>
                    </>
                  )}
                  {showModelActions && (
                    <>
                      {showContentActions && <DropdownDivider />}
                      {canExportModel && (
                        <DropdownItem onClick={handleExportModel}>
                          <ArrowDownTrayIcon />
                          <DropdownLabel>Export Model</DropdownLabel>
                        </DropdownItem>
                      )}
                      {canImportModel && (
                        <DropdownItem onClick={() => importInputRef.current?.click()}>
                          <ArrowUpTrayIcon />
                          <DropdownLabel>Import Model</DropdownLabel>
                        </DropdownItem>
                      )}
                    </>
                  )}
                  {showResetAllPages && (
                    <>
                      <DropdownDivider />
                      <DropdownItem onClick={() => rebuildAllCategoriesMutation.mutate()}>
                        <DropdownLabel className="text-red-600">Reset All Pages</DropdownLabel>
                      </DropdownItem>
                    </>
                  )}
                </DropdownMenu>
              </Dropdown>
            )}
            {canManageCategories && (
              <Button color="indigo" onClick={() => navigate('/categories/new')}>
                <PlusIcon />
                Add Category
              </Button>
            )}
          </AuthGuard>
        </div>
      </PageHeader>

      {/* Filters */}
      {showFilters && <CategoriesFilters filters={filters} setFilters={setFilters} onClear={clearFilters} />}

      <div className="flex flex-col gap-8">
        {showContentActions && showGraph && (
          <CategoryMislabelMap
            emphasizedCategoryIds={emphasizedCategoryIds}
            selectedCategoryIds={selectedCategoryIdSet}
            onClearSelection={() => setSelectedCategoryIds(new Set())}
          />
        )}

        {viewState.kind === 'loading' && (
          <div className={CARD_CLASSES}>
            <TableSkeleton />
          </div>
        )}

        {viewState.kind === 'error' && (
          <div className={CARD_CLASSES}>
            <div className="p-8 text-center text-red-600 dark:text-red-400">
              {fetchError?.message || 'Failed to load categories'}
            </div>
          </div>
        )}

        {viewState.kind === 'ready' && response && (
          <div className={CARD_CLASSES}>
            <div className="overflow-x-auto">
              <SortableTable
                storageId="categories"
                columns={columns}
                data={filteredData}
                defaultSortBy="name"
                rowKey={(item) => item.id.toString()}
                selectedRows={selectedCategoryIds}
                setSelectedRows={setSelectedCategoryIds}
                rowClassName={(item) =>
                  selectedCategoryIds.has(item.id.toString()) ? 'bg-gray-50 dark:bg-zinc-700' : ''
                }
                limitHeight={false}
              />
            </div>
          </div>
        )}

        {viewState.kind === 'filtered-empty' && (
          <div className="text-center text-gray-500 dark:text-zinc-400 mt-6">
            No categories found with the current filters.
            <br />
            <Button className="group/clear-filters mt-2" plain onClick={clearFilters}>
              <span>
                Clear filters
                <span className="group-hover/clear-filters:invisible absolute">?</span>
              </span>
            </Button>
          </div>
        )}

        {viewState.kind === 'empty' && (
          <EmptyCategoriesState
            reason={viewState.reason}
            canImportModel={canImportModel}
            canCreateCategory={canManageCategories}
            isImporting={importLoading}
            onImportModel={() => importInputRef.current?.click()}
            onCreateCategory={() => navigate('/categories/new')}
          />
        )}

        <AuthGuard roles={['admin']}>
          <ModelRegistryCard />
        </AuthGuard>
      </div>
    </div>
  );
}
